import Button from '@atlaskit/button';
import { WidthObserver } from '@atlaskit/width-detector';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import Store from '../../store';
import BlanketStore from '../../store/blanketStore';
import ProductStore from '../../store/productStore';
import UIStore from '../../store/uiStore';

import CardRow from './CardRow';
import { getSiteAdminLink } from './LinkCard/helpfulLinks';
import { getVisibleProductsCount } from './util';

const SitesContainer = styled.div`
  margin-top: 16px;
`;

type InjectedProps = AnalyticsConsumerProps;

type Props = {
  blanketStore: BlanketStore;
  containerWidth: number;
  productStore: ProductStore;
  uiStore: UIStore;
} & InjectedProps;

@observer
class ProductScreen extends React.Component<Props> {
  componentDidMount() {
    when(
      () => this.props.productStore.hasLoaded,
      () => {
        const { isUserAdmin, joinableProducts, availableProducts } = this.props.productStore;
        void this.props.analytics.page('allProductsScreen', {
          availableProductsCount: availableProducts.length,
          isUserAdmin,
          joinableProductsCount: joinableProducts.length,
        });
      },
    );
  }

  handleBackButtonClick = () => {
    this.props.analytics.ui('button', 'clicked', {}, { actionSubjectId: 'backButton' });
    this.props.blanketStore.hideBlanket();
  };

  render() {
    const { containerWidth } = this.props;
    const { joinableProducts, joinableSites, availableProducts, availableSites } = this.props.productStore;
    const { mobileMode } = this.props.uiStore;

    const renderSites = availableSites.length > 0 ? availableSites : joinableSites;
    const renderProducts = availableProducts.length > 0 ? availableProducts : joinableProducts;

    // Edge case: If user goes to full page view and resize the screen so it's big enough to fit all of the apps
    // We need to ensure we don't try to fit more invisible cards than necessary
    const maxCardsPerRow = Math.min(renderProducts.length, getVisibleProductsCount(containerWidth, mobileMode));
    const { links, linkConfiguration } = this.props.productStore;

    return (
      <React.Fragment>
        {availableSites.length > 0 && (
          <CardRow
            action={this.renderBackButton()}
            data-test-id="frequent-card-row"
            fullScreen
            header={<FormattedMessage {...messages.productsHeader} />}
            products={renderProducts}
            singleRow
            maxCardsPerRow={maxCardsPerRow}
          />
        )}
        <CardRow
          action={availableSites.length === 0 ? this.renderBackButton() : undefined}
          data-test-id="helpful-links-row"
          header={<FormattedMessage {...messages.linksHeader} />}
          links={links}
          fullScreen
          hideSiteName
          maxCardsPerRow={maxCardsPerRow}
        />
        <SitesContainer>
          {renderSites.map(site => {
            return site.products.length > 0 ? (
              <CardRow
                data-test-id="site-row"
                fullScreen
                key={`product-row-${site.name}`}
                links={
                  site.adminAccess &&
                  /**
                   * TEMPORARY FIX to address
                   * https://ops.internal.atlassian.com/jira/projects/HOT/queues/issue/HOT-99106
                   * Don't show admin link if site only has Opsgenie or Statuspage products as invalid
                   * Clean up ticket: https://product-fabric.atlassian.net/browse/ACT-3002
                   */
                  site.products.filter(product => !['OPSGENIE', 'STATUSPAGE'].includes(product.productKey)).length > 0
                    ? [getSiteAdminLink(site.url, linkConfiguration)]
                    : []
                }
                header={site.name}
                products={site.products}
                hideSiteName
                maxCardsPerRow={maxCardsPerRow}
              />
            ) : null;
          })}
        </SitesContainer>
      </React.Fragment>
    );
  }

  renderBackButton() {
    return (
      <Button appearance="link" spacing="none" onClick={this.handleBackButtonClick} data-test-id="blanket-back-button">
        <FormattedMessage {...messages.backToHomePage} />
      </Button>
    );
  }
}

const messages = defineMessages({
  backToHomePage: {
    defaultMessage: 'Back',
    description: 'Return to home page',
    id: 'uchi.backToHomePage',
  },
  linksHeader: {
    defaultMessage: 'Helpful links',
    description: 'Helpful links header',
    id: 'uchi.helpfulLinksHeader',
  },
  productsHeader: {
    defaultMessage: 'Frequent',
    description: 'Frequent products header',
    id: 'uchi.frequentHeader',
  },
});

export const InjectedProductScreen = inject(({ blanketStore, productStore, uiStore }: Store) => ({
  blanketStore,
  productStore,
  uiStore,
}))(withAnalytics(ProductScreen));

export default () => {
  const [width, setWidth] = useState<number>();
  return (
    <>
      <WidthObserver setWidth={setWidth} />
      {typeof width === 'number' ? <InjectedProductScreen containerWidth={width} /> : undefined}
    </>
  );
};
